import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { planService } from '../../services/planService';
import { BrandLoadingScreen } from '../brand/BrandLoadingScreen';

interface SubscriptionGuardProps {
  children: React.ReactNode;
}

const PUBLIC_PAGES = [
  '/brand/auth',
  '/brand/pricing', // Allow pricing page so users can subscribe
  '/brand/settings' // Allow settings page so users can subscribe
];

export const SubscriptionGuard = ({ children }: SubscriptionGuardProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isDoingFinalCheck, setIsDoingFinalCheck] = useState(false);

  // Check if current page is public (doesn't require subscription)
  const isPublicPage = PUBLIC_PAGES.some(path => 
    location.pathname.startsWith(path)
  );

  // Only check subscription for protected pages
  const { data: subscription, isLoading, error, refetch } = useQuery({
    queryKey: ['subscription'],
    queryFn: () => planService.getCurrentSubscription(),
    enabled: !isPublicPage, // Only run query for protected pages
    refetchOnWindowFocus: false,
    retry: 2,
    retryDelay: 1000
  });

  const hasActiveSubscription = !!subscription?.data?.active;

  useEffect(() => {
    // Don't redirect if:
    // 1. Still loading
    // 2. On a public page  
    // 3. Already have active subscription
    // 4. There was an error (let user try to access the page)
    // 5. Already doing final check
    if (isLoading || isPublicPage || hasActiveSubscription || error || isDoingFinalCheck) {
      return;
    }

    // Do one final refetch and check before navigating
    const doFinalCheckAndRedirect = async () => {
      // Logging out clears the token and navigates to the login page; the
      // subscription query then errors/empties and this guard would race the
      // logout navigation with a bounce to /brand/pricing (visible as a
      // pricing flash + a flaky e2e logout test). A missing token means
      // "logged out", not "unsubscribed" — leave the navigation alone.
      if (!localStorage.getItem('brand_token')) {
        return;
      }
      setIsDoingFinalCheck(true);

      try {
        console.log('Doing final subscription check before redirect...');
        const finalResult = await refetch();

        // Check if subscription is now active after final refetch
        const finalHasActiveSubscription = !!finalResult.data?.data?.active;

        if (finalHasActiveSubscription) {
          console.log('Active subscription found on final check, staying on page');
          setIsDoingFinalCheck(false);
          return;
        }

        // The token can also disappear while the refetch was in flight.
        if (!localStorage.getItem('brand_token')) {
          setIsDoingFinalCheck(false);
          return;
        }

        // No active subscription found even after final check - redirect to pricing
        console.log('No active subscription found after final check, redirecting to pricing page');
        toast('Please subscribe to access other pages', {
          icon: '💼',
          duration: 4000
        });
        navigate('/brand/pricing', { replace: true });
      } catch (error) {
        console.error('Final subscription check failed:', error);
        // On error, let the user stay on the page (maybe they can still access it)
        setIsDoingFinalCheck(false);
      }
    };

    doFinalCheckAndRedirect();
  }, [isLoading, isPublicPage, hasActiveSubscription, error, isDoingFinalCheck, refetch, navigate]);

  // Show loading only when checking subscription for protected pages or doing final check
  if (!isPublicPage && (isLoading || isDoingFinalCheck)) {
    return <BrandLoadingScreen />;
  }

  // Render children - the redirect logic above will handle subscription enforcement
  return <>{children}</>;
};